import { useMemo } from "react";
import { getShortCode, Language } from "../models/Locale";
import { getLocale, load } from "../snippets/localize";
import { useLocalizationStore } from "../stores/localizationStore";
import { useSeller, useUser } from "./useAccount";

export const useLocalization = () => {
    const seller = useSeller();
    const user = useUser();

    const availableLanguages = useLocalizationStore((state) => state.availableLanguages);
    const setAvailableLanguages = useLocalizationStore((state) => state.setAvailableLanguages);

    const locale = useMemo(() => {
        return getLocale(user, seller);
    }, [user, seller]);

    const initialize = async () => {
        await load();

        const detectedLanguageList = await fetchLanguageList();

        // On some operating systems there may be a bug when loading the language for the first time.
        // If the language does not match the language selected in the application, we additionally set it here.
        if (detectedLanguageList.some((language) => getShortCode(language) !== getShortCode(locale))) {
            setTimeout(() => {
                setLanguage(getShortCode(locale));
            }, 600);
        }

        if (!window.Localize || availableLanguages.length > 0) return;

        const languages = await fetchAvailableLanguages();

        setAvailableLanguages(languages);
    };

    return { locale, initialize, setLanguage };
};

const setLanguage = (locale: string) => {
    window.Localize?.setLanguage(locale);
};

const fetchAvailableLanguages = async (): Promise<Language[]> => {
    return new Promise((resolve, reject) => {
        window.Localize.getAvailableLanguages((error: any, languages: Language[]) => {
            if (error) {
                console.error("Error in fetching available languages", error);
                reject(error);
            }

            resolve(languages);
        });
    });
};

const fetchLanguageList = async (): Promise<string[]> => {
    return new Promise((resolve, reject) => {
        window.Localize.detectLanguage((error: any, languages: string[]) => {
            if (error) {
                console.error("Error in fetching available languages", error);
                reject(error);
            }

            resolve(languages);
        });
    });
};
